import { buildDaySchedule } from '@/domain/queueEngine';
import { resolveDayOff } from '@/domain/artistAdmin';
import type { Artist, Booking } from '@/domain/types';
import type { AvailabilityCellKind } from '@/components/queue/availabilityCell';

/** One run of the working day, left to right: time in the chair, or time open. */
export interface MonthDaySegment {
  kind: 'booked' | 'open';
  hours: number;
}

export interface ArtistMonthDay {
  /** Local start of the day. */
  dayIso: string;
  dayOfMonth: number;
  /** False for the adjacent-month days that pad the grid out to whole weeks. */
  inMonth: boolean;
  kind: AvailabilityCellKind;
  /** Comp / leave note, when the day off carries one. */
  note?: string;
  /** Whole hours open, rounded — the figure the cell shows. */
  openHours: number;
  rosteredHours: number;
  /** The shape of the day. Empty on a day off. */
  segments: MonthDaySegment[];
  past: boolean;
  isToday: boolean;
}

export interface ArtistMonth {
  /** The 1st of the displayed month. */
  monthStartIso: string;
  /** The Monday of the week holding the 1st — where the grid begins. */
  gridStartIso: string;
  /** Whole weeks, so always a multiple of seven. */
  days: ArtistMonthDay[];
}

function startOfLocalDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

/** Monday-first: how many days back the week containing `day` began. */
function daysSinceMonday(day: Date): number {
  return (day.getDay() + 6) % 7;
}

/**
 * Turn `buildDaySchedule`'s blocks into left-to-right runs across the rostered
 * window. Booked runs come from the blocks (real sittings plus the synthetic
 * filler the schedule already carries); everything between them is open.
 * Adjacent runs of the same kind merge, so the shape reads as the day does.
 */
function segmentsFor(
  blocks: Array<{ startHour: number; endHour: number }>,
  startHour: number,
  endHour: number,
): MonthDaySegment[] {
  const out: MonthDaySegment[] = [];
  const push = (kind: MonthDaySegment['kind'], hours: number) => {
    if (hours <= 0.0001) return;
    const last = out[out.length - 1];
    if (last && last.kind === kind) {
      last.hours += hours;
      return;
    }
    out.push({ kind, hours });
  };

  let cursor = startHour;
  for (const b of blocks) {
    const blockStart = Math.max(cursor, b.startHour);
    const blockEnd = Math.min(endHour, b.endHour);
    if (blockStart > cursor) push('open', blockStart - cursor);
    if (blockEnd > blockStart) push('booked', blockEnd - blockStart);
    cursor = Math.max(cursor, Math.min(endHour, b.endHour));
  }
  if (cursor < endHour) push('open', endHour - cursor);

  return out;
}

/**
 * One artist's month as a Monday-first grid of whole weeks: how many hours each
 * day has open, and where in the day those hours sit.
 *
 * Every working day is derived from `buildDaySchedule` — the same function that
 * draws the day breakdown under the grid, and the same one the sweeper uses — so
 * a cell's figure and its drill-down cannot disagree, and the artist's view of a
 * gap matches the view of whoever is filling it. Off days come from
 * `resolveDayOff`, so comp days and leave read here exactly as they do in the
 * agent grids.
 */
export function buildArtistMonth(args: {
  artist: Artist;
  bookings: Record<string, Booking>;
  /** Any moment inside the month to display. */
  monthIso: string;
  /** The simulation clock, for today / past. Never `Date.now()`. */
  nowIso: string;
}): ArtistMonth {
  const anchor = new Date(args.monthIso);
  const monthStart = startOfLocalDay(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  const monthEnd = startOfLocalDay(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0));

  const gridStart = startOfLocalDay(monthStart);
  gridStart.setDate(gridStart.getDate() - daysSinceMonday(monthStart));

  // Whole weeks, enough to reach the end of the month.
  const spanDays = Math.round((monthEnd.getTime() - gridStart.getTime()) / 86_400_000) + 1;
  const gridDays = Math.ceil(spanDays / 7) * 7;

  const today = startOfLocalDay(new Date(args.nowIso));
  const month = monthStart.getMonth();

  const days: ArtistMonthDay[] = [];
  for (let i = 0; i < gridDays; i++) {
    const day = startOfLocalDay(gridStart);
    day.setDate(gridStart.getDate() + i);
    const dayIso = day.toISOString();

    const schedule = buildDaySchedule({ artist: args.artist, bookings: args.bookings, dayIso, nowIso: args.nowIso });
    const dayOff = resolveDayOff(args.artist.availability, day);
    const kind: AvailabilityCellKind = dayOff
      ? dayOff.kind
      : schedule.working
        ? 'working'
        : 'rdo';

    const working = kind === 'working' ? schedule.working : null;
    const openHours = working
      ? Math.round(schedule.freeWindows.reduce((sum, w) => sum + w.hours, 0))
      : 0;

    days.push({
      dayIso,
      dayOfMonth: day.getDate(),
      inMonth: day.getMonth() === month,
      kind,
      ...(dayOff?.note ? { note: dayOff.note } : {}),
      openHours,
      rosteredHours: working ? working.endHour - working.startHour : 0,
      segments: working
        ? segmentsFor(schedule.bookings, working.startHour, working.endHour)
        : [],
      past: day.getTime() < today.getTime(),
      isToday: day.getTime() === today.getTime(),
    });
  }

  return {
    monthStartIso: monthStart.toISOString(),
    gridStartIso: gridStart.toISOString(),
    days,
  };
}
